import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutDashboard, ShoppingCart, FileText, ClipboardList, FileCheck2, Truck,
  RotateCcw, Receipt, Users, Package, Warehouse, Wallet, TrendingUp, Landmark,
   Percent, BarChart3, Settings, ChevronDown, LogOut, Building2, Menu, X, Stethoscope,
   BadgeCheck, ScrollText, ShieldCheck, Bell, BookOpen, PieChart, UserRoundSearch, Library,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TAX_REPORTS } from "@/lib/gstReports";
import { REPORT_GROUPS, reportPath } from "@/lib/reportCatalog";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, to: "/" },
  {
    label: "Sales", icon: ShoppingCart, section: "SALES", children: [
      { label: "Sales Invoices", to: "/sales/invoices", icon: FileText },
      { label: "Quotations", to: "/sales/quotations", icon: FileCheck2 },
      { label: "Sales Orders", to: "/sales/orders", icon: ClipboardList },
      { label: "Delivery Challans", to: "/sales/delivery-challans", icon: Truck },
      { label: "Credit Notes", to: "/sales/credit-notes", icon: RotateCcw },
      { label: "POS Billing", to: "/pos", icon: Receipt },
    ],
  },
  {
    label: "Purchases", icon: ShoppingCart, section: "PURCHASE", children: [
      { label: "Purchase Invoices", to: "/purchases/invoices", icon: FileText },
      { label: "Purchase Orders", to: "/purchases/orders", icon: ClipboardList },
      { label: "Purchase Returns", to: "/purchases/returns", icon: RotateCcw },
      { label: "Purchase Payments", to: "/purchases/payments", icon: Wallet },
      { label: "Vendors", to: "/purchases/vendors", icon: Building2 },
    ],
  },
  {
    label: "Parties", icon: Users, section: "PARTIES", children: [
      { label: "Customers", to: "/parties/customers", icon: Users },
      { label: "Vendors", to: "/purchases/vendors", icon: Building2 },
    ],
  },
  { label: "Products", icon: Package, to: "/products" },
  { label: "Catalogue", icon: Library, to: "/catalogue" },
  { label: "Inventory", icon: Warehouse, to: "/inventory" },
  { label: "Payments", icon: Wallet, to: "/payments" },
  { label: "Expenses", icon: Receipt, to: "/expenses" },
  { label: "Approvals", icon: BadgeCheck, to: "/approvals" },
  { label: "Recruitment", icon: UserRoundSearch, to: "/recruitment", perm: "manage_users" },
  {
    label: "Legal", icon: ScrollText, section: "Legal", children: [
      { label: "Certificates", to: "/certificates", icon: ShieldCheck },
      { label: "Agreements", to: "/agreements", icon: ScrollText },
      { label: "Letters", to: "/legal-letters", icon: FileText, perm: "manage_masters" },
    ],
  },
  { label: "Medical CRM", icon: Stethoscope, to: "/crm" },
  { label: "Transport", icon: Truck, to: "/transport" },
  {
    label: "Reports", icon: BarChart3, section: "REPORTS", perm: "view_financials", children: [
      { label: "Reports Center", to: "/reports", icon: BarChart3 },
      { label: "Daybook", to: reportPath("daybook"), icon: BookOpen },
    ],
  },
  ...REPORT_GROUPS.slice(1).map((group, index) => ({
    label: group.label,
    icon: index === 0 ? PieChart : TrendingUp,
    section: "REPORTS",
    perm: "view_financials",
    reportGroup: true,
    children: group.reports.map((report) => ({
      label: report.label,
      to: reportPath(report.key),
      wrap: true,
    })),
  })),
  {
    label: "Taxes", icon: Percent, section: "REPORTS", perm: "view_financials", children: [
      { label: "All Tax Reports", to: "/reports/taxes", icon: BarChart3 },
      ...TAX_REPORTS.map(([key, label]) => ({ label, to: `/gst/reports?report=${key}` })),
      { label: "E-Way Bills", to: "/gst/eway-bills", icon: Truck },
    ],
  },
  { label: "Settings", icon: Settings, to: "/settings", perm: "manage_settings" },
];

function ItemLink({ item }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const base = "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors";
  if (item.soon) {
    return (
      <button
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        onClick={() => toast.info(`${item.label} module — coming soon`)}
        className={cn(base, "text-slate-400 hover:bg-slate-100 w-full text-left")}
      >
        {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
        <span className="truncate">{item.label}</span>
        <span className="ml-auto text-[9px] uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">soon</span>
      </button>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        cn(base, (isActive && (!item.to.includes("?") ||
          new URLSearchParams(search).get("report") === new URLSearchParams(item.to.split("?")[1]).get("report")))
          ? "bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
      }
    >
      {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
      <span className={item.wrap ? "min-w-0 leading-snug break-words" : "truncate"}>{item.label}</span>
    </NavLink>
  );
}

function Group({ item }) {
  const [open, setOpen] = useState(false);
  const { can } = useAuth();
  const children = item.children.filter((child) => !child.perm || can(child.perm));
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex items-center gap-3 px-3 py-2 w-full rounded-md text-sm text-slate-700 hover:bg-slate-100"
        data-testid={`navgroup-${item.label.toLowerCase()}`}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        <span className="font-medium">{item.label}</span>
        <ChevronDown className={cn("h-4 w-4 ml-auto transition-transform", !open && "-rotate-90")} />
      </button>
      {open && (
        <div className="ml-3 pl-2 border-l border-slate-200 mt-0.5 space-y-0.5">
          {children.map((c) => <ItemLink key={c.label} item={c} />)}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const allow = (it) => !it.perm || can(it.perm);
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  const sidebar = (
    <div className="flex flex-col h-full bg-white border-r border-slate-200 w-64 shrink-0">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-200">
        <div className="h-9 w-9 rounded-lg bg-brand grid place-items-center shadow-sm">
          <Stethoscope className="h-5 w-5 text-brand-foreground" />
        </div>
        <div>
          <div className="font-heading font-bold text-slate-800 leading-tight tracking-tight">VGRUPS</div>
          <div className="text-[10px] uppercase tracking-widest text-brand-600">Health Care ERP</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((item) =>
          !allow(item) ? null : item.children ? <Group key={item.label} item={item} /> : <ItemLink key={item.label} item={item} />
        )}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-8 w-8 rounded-full bg-brand-100 text-brand-700 grid place-items-center text-sm font-semibold">
            {(user?.name || "G")[0]}
          </div>
          <div className="min-w-0">
            <div className="text-sm text-slate-800 truncate">{user?.name || "Guest"}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.role || "Guest"}</div>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-button"
            className="ml-auto text-slate-400 hover:text-rose-500 p-1.5">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-background">
      <div className="hidden lg:block">{sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button type="button" aria-label="Close navigation menu" className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full max-h-[100dvh]">{sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8 gap-4 shrink-0 overflow-hidden">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button aria-label="Open navigation menu" className="lg:hidden grid h-11 w-11 shrink-0 place-items-center rounded-md hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </button>
            <div className="text-sm text-slate-500 hidden xl:block truncate" data-testid="header-current-date">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
          <div className="hidden min-w-0 flex-[2] justify-center xl:flex no-print" data-testid="header-ticker">
            <style>{`@keyframes vgticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}} .vg-ticker{display:flex;width:max-content;white-space:nowrap;animation:vgticker 28s linear infinite;}`}</style>
            <div className="w-full max-w-[640px] overflow-hidden rounded-full border border-brand-200 bg-brand-50/80 px-5 py-1.5 text-xs font-medium text-slate-600 shadow-sm">
              <div className="vg-ticker">
                {[0, 1].map((k) => (
                  <span key={k} className="flex items-center shrink-0">
                    <span className="mx-5">www.vgrups.com</span><span className="text-brand-500">•</span>
                    <span className="mx-5">www.vgrupsoncology.com</span><span className="text-brand-500">•</span>
                    <span className="mx-5">Toll-free: 1800 889 3896</span><span className="text-brand-500">•</span>
                    <span className="mx-5">Accounts: 8333881144</span><span className="text-brand-500">•</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
            <button onClick={() => navigate("/notifications")} data-testid="header-notifications" className="relative p-2 rounded-md hover:bg-slate-100" title="Notifications">
              <Bell className="h-4 w-4 text-slate-600" />
            </button>
            <button onClick={() => navigate("/select-company")} data-testid="company-switcher"
              className="flex min-w-0 items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 max-w-[200px]">
              <Building2 className="h-4 w-4 text-brand-600 shrink-0" />
              <span className="truncate">{localStorage.getItem("vg_company_name") || "Select Company"}</span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            </button>
            <span className="text-xs font-mono px-2.5 py-1 rounded-md bg-brand-50 text-brand-700 border border-brand-200 hidden 2xl:inline" data-testid="header-gstin">
              GSTIN 36ABCDE1234F1Z5
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-background">{children}</main>
      </div>
    </div>
  );
}
